import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Calendar,
  ChevronRight,
  Clock,
} from 'lucide-react'
import { Section } from '@/components/Section'
import { NewsCard } from '@/components/NewsCard'
import { LocalizedLink } from '@/components/LocalizedLink'
import { JsonLd } from '@/components/SEO/JsonLd'
import { usePageSEO } from '@/hooks/usePageSEO'
import type { SupportedLanguage } from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import {
  getNewsBySlug,
  getRelatedNews,
  newsKey,
  type NewsArticle,
} from '@/data/news'

/* ============================================================
   Helpers
   ============================================================ */

/**
 * Rough reading-time estimate. Uses a conservative 200 wpm baseline;
 * clamped to a minimum of 1 minute.
 */
function estimateReadingTime(text: string): number {
  const words = text.trim().split(/\s+/).length
  return Math.max(1, Math.round(words / 200))
}

function formatLongDate(iso: string, lang: SupportedLanguage): string {
  const parsed = new Date(iso)
  if (isNaN(parsed.getTime())) return iso
  return new Intl.DateTimeFormat(lang === 'it' ? 'it-IT' : 'en-GB', {
    dateStyle: 'long',
  }).format(parsed)
}

/* ============================================================
   Breadcrumb — industrial, block-based, same family as machines
   ============================================================ */
function Breadcrumb({ articleTitle }: { articleTitle: string }) {
  const { t } = useTranslation()
  return (
    <nav
      aria-label="Breadcrumb"
      className="border-b border-ink-200 bg-white"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
        <ol
          role="list"
          className="flex flex-wrap items-center gap-1.5 text-xs font-semibold tracking-[0.02em] text-ink-500"
        >
          <li>
            <LocalizedLink
              to="home"
              className="uppercase tracking-[0.14em] text-ink-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              {t('newsDetail.breadcrumbHome')}
            </LocalizedLink>
          </li>
          <li aria-hidden="true" className="text-ink-300">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </li>
          <li>
            <LocalizedLink
              to="news"
              className="uppercase tracking-[0.14em] text-ink-600 transition-colors hover:text-brand-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              {t('newsDetail.breadcrumbNews')}
            </LocalizedLink>
          </li>
          <li aria-hidden="true" className="text-ink-300">
            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
          </li>
          <li
            aria-current="page"
            className="text-ink-900 font-bold tracking-normal normal-case line-clamp-1 max-w-[28ch] md:max-w-none"
          >
            {articleTitle}
          </li>
        </ol>
      </div>
    </nav>
  )
}

/* ============================================================
   Article hero — full-width image + dark overlay + title overlay
   ============================================================ */
function ArticleHero({
  article,
  title,
  alt,
  currentLang,
}: {
  article: NewsArticle
  title: string
  alt: string
  currentLang: SupportedLanguage
}) {
  const { t } = useTranslation()
  const dateFormatted = formatLongDate(article.date, currentLang)

  return (
    <section
      aria-labelledby="news-article-title"
      className="relative isolate overflow-hidden bg-ink-900 text-white"
    >
      {/* Background image */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-20 bg-cover bg-center"
        style={{ backgroundImage: `url(${article.image})` }}
      />
      {/* Role-based gradient overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 bg-gradient-to-b from-ink-900/80 via-ink-900/82 to-ink-900/95"
      />
      {/* Brand multiply tint */}
      <div
        aria-hidden="true"
        className="absolute inset-0 -z-10 mix-blend-multiply bg-brand-900/35"
      />
      {/* Diagonal stripe accent — bleeding in from the right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-2/5 opacity-[0.1] fx-diagonal-accent"
      />
      {/* Radial brand glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-40 top-2/3 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 pt-20 md:pt-28 pb-16 md:pb-20">
        {/* Left engineering tick rail */}
        <div
          aria-hidden="true"
          className="absolute left-4 sm:left-6 lg:left-8 top-16 md:top-24 flex flex-col gap-1.5 text-brand-400"
        >
          <span className="block h-3 w-px bg-current" />
          <span className="block h-6 w-px bg-current" />
          <span className="block h-3 w-px bg-current" />
        </div>

        <div className="pl-6 sm:pl-8 lg:pl-10 max-w-4xl">
          {/* Eyebrow + tags row */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-2 rounded-sm border border-brand-400/30 bg-brand-500/10 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.26em] text-brand-200 backdrop-blur-sm">
              <span
                aria-hidden="true"
                className="block h-1.5 w-1.5 rounded-full bg-brand-400"
              />
              {t('newsDetail.breadcrumbNews')}
            </span>
            {article.tagKeys?.map((tagKey) => (
              <span
                key={tagKey}
                className="rounded-sm border border-white/15 bg-white/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 backdrop-blur-sm"
              >
                {t(`newsData.tags.${tagKey}`)}
              </span>
            ))}
          </div>

          <h1
            id="news-article-title"
            className="fx-text-balance font-bold leading-[1.08] text-white text-3xl sm:text-4xl md:text-5xl"
          >
            {title}
          </h1>

          {/* Meta row */}
          <dl className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4 text-sm">
            <div className="flex items-center gap-2.5">
              <Calendar
                className="h-4 w-4 text-brand-300"
                strokeWidth={2.25}
                aria-hidden="true"
              />
              <dt className="sr-only">{t('newsDetail.publishedOn')}</dt>
              <dd className="font-semibold uppercase tracking-[0.14em] text-white/85">
                <time dateTime={article.date}>{dateFormatted}</time>
              </dd>
            </div>
          </dl>

          {/* Image alt — visually hidden for screen readers */}
          <span className="sr-only">{alt}</span>
        </div>

        {/* Bottom brand rule */}
        <div
          aria-hidden="true"
          className="absolute bottom-0 left-4 sm:left-6 lg:left-8 h-[3px] w-24 bg-brand-500"
        />
      </div>
    </section>
  )
}

/* ============================================================
   Article body — typography block
   ============================================================ */
function ArticleBody({
  paragraphs,
  readingMinutes,
  article,
}: {
  paragraphs: string[]
  readingMinutes: number
  article: NewsArticle
}) {
  const { t } = useTranslation()

  return (
    <Section tone="default" spacing="lg">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
        {/* Left column — reading meta */}
        <aside className="lg:col-span-3 lg:sticky lg:top-24">
          <div className="relative border border-ink-200 bg-white p-5 md:p-6">
            <div
              aria-hidden="true"
              className="absolute top-0 left-0 h-[3px] w-12 bg-brand-500"
            />
            <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-brand-600 mb-4">
              {t('newsDetail.publishedOn')}
            </p>
            <dl className="space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <Calendar
                  className="h-4 w-4 mt-0.5 text-brand-500"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
                    {t('newsDetail.publishedOn')}
                  </dt>
                  <dd className="mt-0.5 font-semibold text-ink-800">
                    <time dateTime={article.date}>
                      {formatLongDate(article.date, 'it')}
                    </time>
                  </dd>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock
                  className="h-4 w-4 mt-0.5 text-brand-500"
                  strokeWidth={2.25}
                  aria-hidden="true"
                />
                <div>
                  <dt className="text-[10px] font-bold uppercase tracking-[0.2em] text-ink-500">
                    {t('newsDetail.readingTime')}
                  </dt>
                  <dd className="mt-0.5 font-semibold text-ink-800 tabular-nums">
                    {t('newsDetail.readingTimeValue', { count: readingMinutes })}
                  </dd>
                </div>
              </div>
            </dl>

            {article.tagKeys && article.tagKeys.length > 0 && (
              <div className="mt-6 pt-6 border-t border-ink-200">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-ink-500 mb-3">
                  {t('newsDetail.tags')}
                </p>
                <ul className="flex flex-wrap gap-1.5">
                  {article.tagKeys.map((tagKey) => (
                    <li
                      key={tagKey}
                      className="rounded-sm bg-brand-50 px-2 py-1 text-[10px] font-bold uppercase tracking-[0.16em] text-brand-700"
                    >
                      {t(`newsData.tags.${tagKey}`)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </aside>

        {/* Body column */}
        <div className="lg:col-span-9 max-w-3xl">
          <article
            className="space-y-6 text-base md:text-[17px] leading-[1.75] text-ink-700 fx-text-pretty"
            aria-label={t('newsDetail.title')}
          >
            {/* Lead drop-cap on first paragraph */}
            {paragraphs.map((p, i) => (
              <p
                key={i}
                className={
                  i === 0
                    ? 'first-letter:float-left first-letter:mr-3 first-letter:font-display first-letter:font-bold first-letter:text-brand-600 first-letter:text-6xl first-letter:leading-[0.9] first-letter:pr-1'
                    : ''
                }
              >
                {p}
              </p>
            ))}
          </article>

          {/* Inline back-to-news link */}
          <div className="mt-12 pt-8 border-t border-ink-200">
            <LocalizedLink
              to="news"
              className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
            >
              <ArrowLeft
                className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1"
                strokeWidth={2.75}
              />
              {t('newsDetail.back')}
            </LocalizedLink>
          </div>
        </div>
      </div>
    </Section>
  )
}

/* ============================================================
   Related articles
   ============================================================ */
function RelatedArticles({ current }: { current: NewsArticle }) {
  const { t } = useTranslation()
  const related = useMemo(() => getRelatedNews(current, 2), [current])
  if (related.length === 0) return null

  return (
    <Section tone="muted" spacing="lg">
      <header className="mb-10 md:mb-12 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <p className="mb-3 inline-flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.22em] text-brand-600">
            <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
            {t('newsDetail.related')}
          </p>
          <h2 className="fx-text-balance font-bold leading-tight text-ink-900 text-2xl md:text-3xl">
            {t('newsDetail.related')}
          </h2>
          <p className="mt-3 text-base leading-relaxed text-ink-600 fx-text-pretty">
            {t('newsDetail.relatedLead')}
          </p>
        </div>
        <LocalizedLink
          to="news"
          className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.14em] text-brand-600 transition-colors hover:text-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 rounded-sm"
        >
          {t('newsDetail.back')}
          <ArrowRight
            className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1"
            strokeWidth={2.75}
          />
        </LocalizedLink>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 md:gap-7">
        {related.map((article) => (
          <NewsCard
            key={article.slug}
            slug={article.slug}
            image={article.image}
            imageAlt={t(newsKey(article, 'alt'))}
            title={t(newsKey(article, 'title'))}
            excerpt={t(newsKey(article, 'excerpt'))}
            date={article.date}
          />
        ))}
      </div>
    </Section>
  )
}

/* ============================================================
   404 — "Article not found" fallback
   ============================================================ */
function NewsNotFound({ slug }: { slug: string | undefined }) {
  const { t } = useTranslation()

  return (
    <main id="main-content" className="bg-ink-100">
      <section className="relative isolate overflow-hidden bg-ink-900 text-white pt-28 md:pt-36 pb-24 md:pb-28">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.08] fx-diagonal-accent"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-40 top-1/2 h-96 w-96 -translate-y-1/2 rounded-full bg-brand-500/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="mb-6 flex items-center gap-3 text-[11px] font-bold uppercase tracking-[0.3em] text-brand-300">
              <span aria-hidden="true" className="block h-px w-8 bg-brand-500" />
              <span>{t('errorPage.code404')}</span>
            </div>

            <p className="font-display font-bold tabular-nums text-brand-500/80 text-7xl md:text-8xl leading-none">
              {t('newsDetail.notFound.code')}
            </p>
            <h1 className="mt-5 fx-text-balance font-bold text-3xl sm:text-4xl md:text-5xl leading-[1.1] text-white">
              {t('newsDetail.notFound.title')}
            </h1>
            <p className="mt-5 max-w-xl fx-text-pretty text-base md:text-lg leading-relaxed text-white/80">
              {t('newsDetail.notFound.body')}
            </p>

            {slug && (
              <p className="mt-4 inline-flex items-center gap-2 rounded-sm border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-white/70">
                <span
                  className="block h-1.5 w-1.5 rounded-full bg-brand-400"
                  aria-hidden="true"
                />
                {t('errorPage.slugLabel')} <span className="font-mono normal-case text-white/90">{slug}</span>
              </p>
            )}

            <div className="mt-10 flex flex-wrap gap-4">
              <LocalizedLink
                to="news"
                className="group relative inline-flex items-center gap-3 rounded-sm bg-brand-500 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white shadow-[0_10px_30px_-12px_rgba(74,155,220,0.55)] transition-[background-color,transform] duration-200 hover:bg-brand-400 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                <span
                  aria-hidden="true"
                  className="absolute -left-[3px] top-1/2 block h-6 w-[3px] -translate-y-1/2 bg-white/90 transition-all duration-200 group-hover:h-8"
                />
                {t('newsDetail.notFound.backCta')}
                <ArrowRight className="h-4 w-4" strokeWidth={2.75} />
              </LocalizedLink>
              <LocalizedLink
                to="home"
                className="inline-flex items-center gap-2 rounded-sm border border-white/30 bg-white/5 px-6 py-3.5 text-sm font-bold uppercase tracking-[0.1em] text-white transition-colors duration-200 hover:border-brand-300 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-900"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
                {t('newsDetail.breadcrumbHome')}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>
    </main>
  )
}

/* ============================================================
   Main component
   ============================================================ */
export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const { t, i18n } = useTranslation()
  const currentLang = (i18n.language || 'it') as SupportedLanguage

  const article = useMemo(
    () => (slug ? getNewsBySlug(slug) : undefined),
    [slug]
  )

  // Pre-compute translated strings BEFORE calling usePageSEO so the hook
  // receives the actual values (and keeps the same hook order on miss).
  const title = article ? t(newsKey(article, 'title')) : ''
  const excerpt = article ? t(newsKey(article, 'excerpt')) : ''
  const body = article ? t(newsKey(article, 'body')) : ''
  const alt = article ? t(newsKey(article, 'alt')) : ''

  const alternatePaths = useMemo(() => {
    if (!article) return undefined
    return {
      it: `/it/${localizedRoutes.news.it}/${article.slug}`,
      en: `/en/${localizedRoutes.news.en}/${article.slug}`,
    }
  }, [article])

  usePageSEO({
    title: article
      ? `${title} | ${t('meta.companyName')}`
      : `${t('newsDetail.notFound.title')} | ${t('meta.companyName')}`,
    description: article ? excerpt : t('newsDetail.notFound.body'),
    ogTitle: article
      ? `${title} — ${t('meta.companyName')}`
      : `${t('newsDetail.notFound.title')} — ${t('meta.companyName')}`,
    ogDescription: article ? excerpt : t('newsDetail.notFound.body'),
    ogImage: article?.image ?? '/images/news/global-industrie-2023.jpg',
    ogType: article ? 'article' : 'website',
    alternatePaths,
  })

  // Prepare derived data even when article is missing, to keep hook order.
  const paragraphs = useMemo(() => {
    if (!body) return []
    return body
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter(Boolean)
  }, [body])

  const readingMinutes = useMemo(
    () => (body ? estimateReadingTime(body) : 1),
    [body]
  )

  if (!article) {
    return <NewsNotFound slug={slug} />
  }

  const origin =
    typeof window !== 'undefined'
      ? window.location.origin
      : 'https://fenixgrind.it'
  const newsPath = `/${currentLang}/${localizedRoutes.news[currentLang]}`
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: t('newsDetail.breadcrumbHome'),
        item: `${origin}/${currentLang}`,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: t('newsDetail.breadcrumbNews'),
        item: `${origin}${newsPath}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: title,
        item: `${origin}${newsPath}/${article.slug}`,
      },
    ],
  }

  return (
    <main id="main-content" className="bg-ink-100">
      <JsonLd id="breadcrumb" data={breadcrumbSchema} />
      <Breadcrumb articleTitle={title} />
      <ArticleHero
        article={article}
        title={title}
        alt={alt}
        currentLang={currentLang}
      />
      <ArticleBody
        paragraphs={paragraphs}
        readingMinutes={readingMinutes}
        article={article}
      />

      {/* Inline back CTA block — extra visibility above related */}
      <section
        aria-label={t('newsDetail.back')}
        className="bg-white border-t border-ink-200"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8 md:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <p className="text-sm md:text-base text-ink-600 fx-text-pretty max-w-xl">
            {t('newsDetail.relatedLead')}
          </p>
          <LocalizedLink
            to="news"
            className="group inline-flex items-center gap-3 rounded-sm border-2 border-brand-600 bg-white px-5 py-2.5 text-sm font-bold uppercase tracking-[0.1em] text-brand-700 transition-[background-color,color,transform] duration-200 hover:bg-brand-600 hover:text-white hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={2.5} />
            {t('newsDetail.back')}
            <ArrowUpRight
              className="h-3.5 w-3.5 opacity-0 transition-all duration-200 group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={2.75}
            />
          </LocalizedLink>
        </div>
      </section>

      <RelatedArticles current={article} />
    </main>
  )
}
